import type { ResponseType } from '@/types/common';
import type { IssueChangeItems } from '@/types/issue';
import type { IssueWorkflow } from '@/types/issue/issue-workflow';
import httpUtil from '@/utils/http';
export * from './issue-components';
export * from './issue-field';
export * from './issue-scheme';
export * from './issue-template';
export * from './issue-user-filter';
export * from './issue-versions';
export * from './project';

const request = httpUtil.accurate.request;
const prefix = 'v1/issues';

export const issueApi = {
  /**
   * @description 获取问题列表
   */
  getList(data: {
    pageIndex: number;
    pageSize: number;
    order?: string;
    summary: string;
    id: number;
    project: number[];
    issueType: number[];
    issueStatus: number[];
    assignee: string[];
    collaborators?: number[];
    priority: number[];
    reporter: string[];
    createdAt: string[];
    updatedAt: string[];
    versions: number[];
    dynamicFieldColumn?: string[];
    batchEdit?: boolean;
  }): ResponseType {
    return request({
      url: prefix + '/list',
      method: 'POST',
      data,
    });
  },
  getListAdvanced(data: {
    filterCondition?: {
      filterMode: 'and' | 'or';
      conditions: {
        field: string;
        operator: string;
        value: string;
      }[];
    };
    orderByCondition?: {
      field: string;
      type: 'asc' | 'desc';
    };
    pageIndex: number;
    pageSize: number;
    order?: string;
    summary?: string;
    id?: number;
    project?: number[];
    issueType?: number[];
    issueStatus?: number[];
    assignee?: string[];
    priority?: number[];
    reporter?: string[];
    createdAt?: string[];
    updatedAt?: string[];
    versions?: number[];
    dynamicFieldColumn?: string[];
    batchEdit?: boolean;
    issueKey?: string;
  }): ResponseType {
    return request({
      url: prefix + '/listAdvanced',
      method: 'POST',
      data,
    });
  },

  /**
   * @description 获取问题详情
   */
  get(id: number): ResponseType {
    return request({
      url: prefix + '/' + id,
      method: 'GET',
    });
  },

  /**
   * @description 通过 issueKey 获取问题详情
   */
  getByKey(issueKey: string): ResponseType {
    return request({
      url: prefix + '/key/' + issueKey,
      method: 'GET',
    });
  },

  /**
   * @description 创建问题
   */
  create(data: any): ResponseType {
    return request({
      url: prefix,
      method: 'POST',
      data,
    });
  },

  /**
   * @description 批量创建问题
   */
  batchCreate(data: any[]): ResponseType {
    return request({
      url: prefix + '/batchCreate',
      method: 'POST',
      data,
      timeout: 30 * 60 * 1000, // 设置超时时间为30分钟
    });
  },

  /**
   * @description 更新问题
   */
  update(id: number, data: any): ResponseType {
    return request({
      url: prefix + '/' + id,
      method: 'PUT',
      data,
    });
  },

  /**
   * @description 更新问题字段
   */
  updateField(id: number, data: any): ResponseType {
    return request({
      url: prefix + '/' + id + '/field',
      method: 'PUT',
      data,
    });
  },

  /**
   * @description 更新问题附件
   */
  updateAttachment(id: number, data: any): ResponseType {
    return request({
      url: prefix + '/' + id + '/attachment',
      method: 'PUT',
      data,
    });
  },

  /**
   * @description 更新问题状态
   */
  updateStatus(id: number, status: number): ResponseType {
    return request({
      url: prefix + '/' + id + '/status',
      method: 'PUT',
      data: {
        status,
      },
    });
  },

  /**
   * @description 批量更新问题
   */
  batchUpdate(data: any): ResponseType {
    return request({
      url: prefix + '/batchUpdate',
      method: 'PUT',
      data,
    });
  },

  /**
   * @description 获取问题详情
   */
  delete(id: number): ResponseType {
    return request({
      url: prefix + '/' + id,
      method: 'DELETE',
    });
  },

  /**
   * @description 获取问题工作流
   */
  getWorkflow(issueType: number): ResponseType<IssueWorkflow> {
    return request({
      url: prefix + `/workflow`,
      method: 'GET',
      params: {
        issueType,
      },
    });
  },

  /**
   * @description 获取操作日志
   */
  getChangeLog(issueId: number): ResponseType<IssueChangeItems[]> {
    return request({
      url: prefix + '/' + issueId + '/changeItems',
      method: 'POST',
    });
  },

  /**
   * @description 改变问题项目
   */
  change(
    issueId: number,
    data: {
      project: number;
      issueType: number;
      versionIds: number[];
      componentIds: number[];
    },
  ): ResponseType {
    return request({
      url: prefix + '/' + issueId + '/change',
      method: 'PUT',
      data,
    });
  },

  /**
   * @description 批量改变问题项目
   */
  batchChange(data: {
    issueIds: number[];
    project: number;
    issueType: number;
    versionIds: number[];
    componentIds: number[];
  }): ResponseType {
    return request({
      url: prefix + '/batchChange',
      method: 'PUT',
      data,
    });
  },

  /**
   * @description 催办
   */
  remind(issueId: number): ResponseType {
    return request({
      url: prefix + '/' + issueId + '/remind',
      method: 'POST',
    });
  },

  /**
   * @description 关注
   */
  focus(issueId: number, newFocusIds: number[]): ResponseType {
    return request({
      url: prefix + '/' + issueId + '/focus',
      method: 'POST',
      data: {
        newFocusIds,
      },
    });
  },

  /**
   * @description 问题导出
   */
  export(data: { project: number; issueType: number; createdAt: [string, string] }): any {
    return request({
      url: prefix + '/export',
      method: 'POST',
      responseType: 'arraybuffer',
      data,
    });
  },

  /**
   * @description 根据搜索项问题导出
   */
  exportBySearch(data: any): any {
    return request({
      url: prefix + '/exportBySearch',
      method: 'POST',
      responseType: 'arraybuffer',
      data,
    });
  },

  /**
   * @description 获取关联信息
   */
  getRelations(issueId: number): ResponseType {
    return request({
      url: prefix + '/' + issueId + '/relations',
      method: 'GET',
    });
  },

  /**
   * @description 绑定关联信息
   */
  bindRelation(data: { issueId: number; relationIds: number[]; relatedStatus: number }): ResponseType {
    return request({
      url: prefix + '/' + data.issueId + '/bindRelation',
      method: 'POST',
      data,
    });
  },

  /**
   * @description 解绑关联信息
   */
  unbind(data: { issueId: number; unbindIds: number[] }): ResponseType {
    return request({
      url: prefix + '/' + data.issueId + '/unbind',
      method: 'POST',
      data,
    });
  },

  /**
   * @description 获取关联用例信息
   */
  getCaseRelations(issueId: number): ResponseType {
    return request({
      url: prefix + '/' + issueId + '/caseRelations',
      method: 'GET',
    });
  },

  /**
   * @description 绑定关联用例信息
   */
  bindCaseRelation(data: {
    issueId: number[];
    relatedPlanId: number;
    relatedCaseId: number;
    relatedCaseVersion: number;
    relatedCaseName: string;
  }): ResponseType {
    return request({
      url: prefix + '/bindCaseRelation',
      method: 'POST',
      data,
    });
  },

  /**
   * @description 解绑关联用例信息
   */
  unbindCase(data: { issueId?: number; unbindIds: number[] }): ResponseType {
    return request({
      url: prefix + '/unbindCase',
      method: 'POST',
      data,
    });
  },

  /**
   * @description 设置项目标签
   */
  updateIssueTag(data: { issueId: number; tag: string }): ResponseType {
    return request({
      url: prefix + '/tag',
      method: 'POST',
      data,
    });
  },

  /**
   * @description 根据 fWorkItemId 获取 issueKey
   */
  getIssueKeyByFWorkItemId(fWorkItemId: number): ResponseType {
    return request({
      url: prefix + '/fWorkItemId/' + fWorkItemId,
      method: 'GET',
    });
  },
};
